// The heard clock: which moment of the audio context's timeline reaches the
// listener's ears when the frame now being drawn appears on the screen.
//
// The voice is the master clock (ADR 0002). Its samples are numbered by the
// context's frames, and the browser says, through getOutputTimestamp(), which
// context time was leaving the speakers at which performance.now() time. So for
// a frame drawn at rafTime (the requestAnimationFrame time stamp) that appears
// displayOffset later:
//
//   heard time = ts.contextTime + (rafTime + displayOffset - ts.performanceTime) / 1000
//
// in seconds, and the heard frame is heard time · sampleRate (HeardAngle then
// subtracts the voice's own latency_frames). Where the browser gives no
// timestamp (no getOutputTimestamp, or zeros before the output starts), the
// fallback pairs currentTime minus the output latency (outputLatency, else
// baseLatency) with performance.now() the same way.
//
// Both clocks run at the same rate, so what is estimated is one number: the
// offset between them, contextTime − performanceTime / 1000. Smoothing it
// keeps the jitter of the time stamps out of the picture:
//
// - With timestamps, a low-pass filter (time constant SMOOTHING_S) follows slow
//   drift between the audio device's clock and the system clock.
// - In the fallback, currentTime moves in steps (one render quantum or one
//   device buffer), so the offset seen falls between steps and jumps back at
//   each one. Its upper envelope is the true offset; it is kept with a peak
//   hold that sinks slowly, so it can follow drift.
//
// An offset that moves by more than RESET_S at once (a new output device, a
// resume after suspend) is taken as it is. While the context is not running the
// heard time holds, and it never runs backwards by less than RESET_S.

/** The parts of an AudioContext the clock reads. A BaseAudioContext has no timestamps and uses the fallback. */
export interface AudioClock {
  readonly sampleRate: number;
  readonly currentTime: number;
  readonly state?: string;
  readonly baseLatency?: number;
  readonly outputLatency?: number;
  getOutputTimestamp?(): { contextTime?: number; performanceTime?: number };
}

export type HeardPath = "timestamp" | "fallback";

export interface HeardClockOptions {
  /**
   * How long after its requestAnimationFrame time stamp a frame appears, in ms:
   * a number, or "frame" (the default) for one frame interval, measured from the
   * time stamps.
   */
  displayOffsetMs?: number | "frame";
  /** The performance clock (performance.now by default); tests pass their own. */
  now?: () => number;
}

/** Time constant of the offset filter with timestamps, in seconds. */
export const SMOOTHING_S = 0.25;
/** An offset change larger than this, in seconds, is a new timeline, taken at once. */
export const RESET_S = 0.02;
/**
 * How fast the fallback's peak hold sinks, in seconds per second: 0.1 ms/s, twice
 * the drift of a 50 ppm clock. Slower sinks sit closer to the true offset but
 * follow drift more slowly; this one settles about 0.05 ms below it at 60 Hz.
 */
const FALLBACK_SINK = 1e-4;
/** The frame interval assumed before two frames were seen, and the bounds of a measured one, in ms. */
const DEFAULT_FRAME_MS = 1000 / 60;
const MIN_FRAME_MS = 4;
const MAX_FRAME_MS = 100;
/** The share of each new frame interval in the measured one: slow, as any jitter in it moves the picture. */
const FRAME_SMOOTHING = 0.02;

export class HeardClock {
  readonly options: HeardClockOptions;
  private clock: AudioClock | null = null;
  private offset = Number.NaN;
  private pathValue: HeardPath = "fallback";
  private lastRaf = Number.NaN;
  private frameMs = Number.NaN;
  private lastHeard = Number.NaN;

  constructor(options: HeardClockOptions = {}) {
    this.options = options;
  }

  /** Follows another context (or none); the estimate starts over. */
  attach(clock: AudioClock | null): void {
    this.clock = clock;
    this.offset = Number.NaN;
    this.lastHeard = Number.NaN;
  }

  get attached(): AudioClock | null {
    return this.clock;
  }

  /** Which estimate the last call used. */
  get path(): HeardPath {
    return this.pathValue;
  }

  /** The offset estimate, contextTime − performanceTime / 1000, in seconds (NaN before the first call). */
  get clockOffset(): number {
    return this.offset;
  }

  /** The display offset in use, in ms. */
  get displayOffsetMs(): number {
    const d = this.options.displayOffsetMs ?? "frame";
    return d === "frame" ? (Number.isNaN(this.frameMs) ? DEFAULT_FRAME_MS : this.frameMs) : d;
  }

  /**
   * The context time, in seconds, heard when the frame drawn at `rafTime` (ms on
   * the performance clock) appears. NaN while no context is attached.
   */
  heardTime(rafTime: number): number {
    const clock = this.clock;
    if (clock === null) return Number.NaN;
    const dt = Number.isNaN(this.lastRaf) ? 0 : (rafTime - this.lastRaf) / 1000;
    if (dt * 1000 >= MIN_FRAME_MS && dt * 1000 <= MAX_FRAME_MS) {
      this.frameMs = Number.isNaN(this.frameMs) ? dt * 1000 : this.frameMs + (dt * 1000 - this.frameMs) * FRAME_SMOOTHING;
    }
    this.lastRaf = rafTime;

    if (clock.state !== undefined && clock.state !== "running") {
      // Suspended or closed: nothing new reaches the ears.
      return Number.isNaN(this.lastHeard) ? clock.currentTime : this.lastHeard;
    }

    const ts = clock.getOutputTimestamp?.();
    let observed: number;
    let path: HeardPath;
    if (ts && (ts.contextTime ?? 0) > 0 && (ts.performanceTime ?? 0) > 0) {
      observed = ts.contextTime! - ts.performanceTime! / 1000;
      path = "timestamp";
    } else {
      // outputLatency ?? baseLatency, where an outputLatency of 0 (Chrome's before the output starts) counts as unknown.
      const latency = clock.outputLatency || clock.baseLatency || 0;
      const now = this.options.now?.() ?? performance.now();
      observed = clock.currentTime - latency - now / 1000;
      path = "fallback";
    }

    if (path !== this.pathValue || Number.isNaN(this.offset) || Math.abs(observed - this.offset) > RESET_S) {
      this.offset = observed;
      this.lastHeard = Number.NaN;
    } else if (path === "timestamp") {
      this.offset += (observed - this.offset) * (1 - Math.exp(-Math.max(0, dt) / SMOOTHING_S));
    } else {
      this.offset = Math.max(observed, this.offset - FALLBACK_SINK * Math.max(0, dt));
    }
    this.pathValue = path;

    let heard = (rafTime + this.displayOffsetMs) / 1000 + this.offset;
    // Never a step back from smoothing; a real reset cleared lastHeard above.
    if (!Number.isNaN(this.lastHeard) && heard < this.lastHeard && this.lastHeard - heard < RESET_S) heard = this.lastHeard;
    this.lastHeard = heard;
    return heard;
  }
}
